// REQ-078 / REQ-082 — Tab "Brillamos Juntos": ranking de nominaciones de la campaña + KPIs en vivo
// + export Excel multi-hoja y export de ganadores por cascadeo (raw|cascaded, CA-046 / WP-08).
// SIN puntos. Respeta la supresión por anonimato.
//
// T-10 (UAT, 18-ago-2026) — el tab es POR CAMPAÑA y necesita su SELECTOR. Sin él llamaba al backend
// con `campaignId` vacío: recibía el agregado del SCOPE bajo el nombre literal «Brillamos», tres de
// los cinco KPIs pintaban «—» para siempre y el «75» de «personas reconocidas» era el conteo del
// scope y de todos los orígenes. Con campaña elegida, los cinco KPIs salen del resumen de ESA
// campaña y el ranking sale de sus recibos.
//
// El LIBRO EXCEL (las 7 hojas de `02 - Campana brillamos juntos.xlsx`) se arma en
// `brillamosExcel.ts`: ahí viven la forma visual calcada de la plantilla del cliente y los textos
// en los 7 idiomas. Este archivo se queda con lo que sí es suyo — resolver los datos de la campaña
// y disparar la descarga. Antes ambas cosas convivían aquí, y la especificación de estilos ocupaba
// más de la mitad del archivo.
//
// «Todos los reconocimientos» consume el export CRUDO paginado (`winnersApi.exportNominationsRaw`,
// ver `fetchNominacionesCrudasPaginado` en `../../fetchNominacionesCrudas.ts`, compartida con
// `AdminWinners.tsx`), no el Top 50/200 de `ShineRankEntryDto`. Desde el 2026-10-07 (RPT-6) este
// reporte lo recorre SIN el tope de 5000 filas, igual que «Recibidos» y «Exportar ganadores»;
// Ganadores conserva su tope.
//
// «Distribución Fundamental» y «Embudo Adopción» quedaron corregidos en el backend
// (`GetRecognitionCampaignQueryHandler`) tras el bug de producción del 26-ago-2026: los porcentajes
// pasaban de 100 % porque `rankingHabitos` no estaba aislado por campaña, y el embudo usaba la
// plantilla del scope COMPLETO como denominador en vez de la audiencia propia de la campaña.
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  useRecognitionCampaign, useRecognitionCampaignLive, recognitionReportsApi,
  useCampaignSummaryDelReporte, useCampaignPoints, getCampaignName, getFundamentalColor, useCampaigns,
  toFiltrosDeDetalle, winnersApi,
} from '@/api/recognition';
import { descargarBlobComoExcel } from '@/utils/excelDownload';
import { claveDeErrorDeExportacion } from '../exportarLibro';
import { userAdminApi } from '@/api/client';
import { exportarGanadoresAExcel, useResolutoresDeGanador } from '../../admin/exportarGanadores';
import { useEffectiveRoles } from '@/auth/RoleGuard';
import { puedeVerPuntos, campanaConPuntos } from '../puntosDeCampana';
import type {
  RecognitionReportParams, ShineRankEntryDto, RecognitionModalidad, AdopcionDto,
  NominationRawExportRowDto, CampaignReportDto, RankedEntryDto, CampaignSummaryDto, CampaignDto,
  CampaignPointsDto, BrillamosReportExportBody, FiltrosDeDetalle,
} from '@/api/recognition';
import { usePersonNameResolver, useOuNameResolver } from '@/views/AnalyticsView/hooks/useAnalyticsNames';
import {
  ReportStatus, KpiRow, SuppressedNote, withResolvedNames, filtrosActivosDe, conFiltrosDeTira,
} from '../reportsShared';
import type { KpiItem } from '../reportsShared';
import { AvisoFiltroNoAplica } from '../AvisoFiltroNoAplica';
import type { FiltroDeTira } from '../AvisoFiltroNoAplica';
import { DistribucionFundamental } from './DistribucionFundamental';
// `pctAdopcion` se REUSA del Diario, no se copia: es la misma regla («plantilla 0 = desconocida, no
// grupo vacío») y tenerla dos veces garantizaría que un día divergieran.
import { pctAdopcion } from './DailyTab';
import { useFundamentalsForReporting } from '@/hooks/useFundamentalsForReporting';
import { Paginacion, usePaginacion } from '../Paginacion';
import { tasaDeParticipacion, formatTasaDisplay } from './CampaignsGeneralTab';
import { fetchNominacionesDeLasCampanas, fetchRecibidosPaginado } from '../../fetchNominacionesCrudas';
import { exportMultiSheetExcel } from '@/utils/exportExcelMultiSheet';
import type { ExcelCellValue, ExcelSheetSpec } from '@/utils/exportExcelMultiSheet';
import { excelColors, argbACss } from '@/utils/excelTheme';
import { resolverFundamentalesConHabitos } from '../../fundamentalesHabitosMatrix';
import type { FundamentalConHabitosResueltos } from '../../fundamentalesHabitosMatrix';
import {
  SIN_DATO, buildDefinicionesSheet, buildDistribucionFundamentalSheet, buildEmbudoSheet,
  buildHojaSinDatos, buildRankingSheet, buildResumenEjecutivoSheet, buildTodosLosReconocimientosSheet,
} from './brillamosExcel';
import type {
  FilaRankingBrillamos, KpisBrillamos, MetadatosBrillamos, Traductor,
} from './brillamosExcel';

/**
 * Porcentaje de adopción de un segmento como FRACCIÓN 0-1 — el formato `0.00%` de Excel hace la
 * conversión, y así la celda sigue siendo un número ordenable en vez del texto `"50.00%"` que se
 * alineaba del lado contrario a los conteos de su misma columna.
 *
 * `null` = censo sin responder (contrato de `AdopcionDto`): cae al guion de «sin dato», nunca a un
 * 0 % que afirmaría que nadie de ese grupo reconoció.
 */
function fraccionDeAdopcion(dato: AdopcionDto | null | undefined): ExcelCellValue {
  const pct = pctAdopcion(dato);
  return pct === null ? SIN_DATO : pct / 100;
}

/** Detalle («N de M han nominado») de una fila de adopción; vacío si el censo no respondió. */
function detalleDeAdopcion(dato: AdopcionDto | null | undefined, t: TFunction<'recognition'>): string {
  if (pctAdopcion(dato) === null) return '';
  return t('reports.shine.adoptionHint', {
    reconocedores: dato!.reconocedores,
    plantilla: dato!.plantilla,
  });
}

/**
 * Los KPI del «Resumen Ejecutivo», ya resueltos a número (o al guion de «sin dato»). Cuanto la hoja
 * necesita saber de la campaña se resuelve aquí; `brillamosExcel.ts` solo los pinta.
 *
 * Objeto de opciones y no cinco parámetros posicionales: `summary`, `isSummarySuprimido` y
 * `tasaParticipacion` describen el mismo resumen y en posición serían fáciles de cruzar.
 */
function kpisDelLibro(opciones: {
  report: CampaignReportDto;
  summary: CampaignSummaryDto | undefined;
  isSummarySuprimido: boolean;
  tasaParticipacion: number | null;
  t: TFunction<'recognition'>;
  /** D4 / Error 3 — el valor y el detalle de la fila de puntos, ya resueltos (`puntosParaElLibro`). */
  puntos: PuntosDelLibro;
}): KpisBrillamos {
  const { report, summary, isSummarySuprimido, tasaParticipacion, t, puntos } = opciones;
  // `plantilla <= 0` es DESCONOCIDO, no «grupo vacío» (contrato de `AdopcionDto`): `pctAdopcion` ya
  // devuelve `null` en ese caso y sirve de guarda para las tres filas que salen de este bloque.
  const totalAdopcion = report.adopcionTotal;
  const pctTotalAdopcion = pctAdopcion(totalAdopcion);
  const hayResumen = !!summary && !isSummarySuprimido;

  return {
    pares: report.pares ?? SIN_DATO,
    lideres: report.lideres ?? SIN_DATO,
    adopcionStaff: fraccionDeAdopcion(report.adopcionStaff),
    detalleAdopcionStaff: detalleDeAdopcion(report.adopcionStaff, t),
    adopcionFrontline: fraccionDeAdopcion(report.adopcionFrontline),
    detalleAdopcionFrontline: detalleDeAdopcion(report.adopcionFrontline, t),
    usuariosTotales: pctTotalAdopcion !== null ? totalAdopcion!.reconocedores : SIN_DATO,
    detalleUsuariosTotales: pctTotalAdopcion !== null
      ? t('reports.shine.adoptionTotalHint', {
          pct: `${pctTotalAdopcion.toFixed(2)}%`, plantilla: totalAdopcion!.plantilla,
        })
      : '',
    // «Sin actividad» se DERIVA de las dos cifras de adopción, no viaja como campo propio: así no
    // puede desincronizarse de ellas.
    sinActividad: pctTotalAdopcion !== null
      ? Math.max(0, totalAdopcion!.plantilla - totalAdopcion!.reconocedores)
      : SIN_DATO,
    reconocimientosEnviados: hayResumen ? summary!.reconocimientos : SIN_DATO,
    totalIndividual: report.totalIndividual ?? SIN_DATO,
    totalGrupal: report.totalGrupal ?? SIN_DATO,
    personasEnGrupales: report.personasEnGrupales ?? SIN_DATO,
    // Solo si están las dos: sumar con un hueco daría un total que parece real y no lo es.
    totalNominaciones: report.totalIndividual != null && report.personasEnGrupales != null
      ? report.totalIndividual + report.personasEnGrupales
      : SIN_DATO,
    totalCrossOu: report.totalCrossOu ?? SIN_DATO,
    nominadosUnicos: hayResumen ? (summary!.nominadosUnicos ?? SIN_DATO) : SIN_DATO,
    participantes: hayResumen ? (summary!.participantes ?? SIN_DATO) : SIN_DATO,
    // Fracción 0-1: `tasaDeParticipacion` devuelve el porcentaje ya multiplicado por cien.
    tasaParticipacion: !isSummarySuprimido && tasaParticipacion != null
      ? tasaParticipacion / 100
      : SIN_DATO,
    plantillaTotal: summary?.audiencia ?? totalAdopcion?.plantilla ?? SIN_DATO,
    plantillaStaff: report.adopcionStaff?.plantilla ?? SIN_DATO,
    plantillaFrontline: report.adopcionFrontline?.plantilla ?? SIN_DATO,
    puntosOtorgados: puntos.valor,
    detallePuntos: puntos.detalle,
  };
}

/**
 * De `ShineRankEntryDto` (lo que manda el backend) a la fila que pinta `buildRankingSheet`. El
 * Fundamental viaja como ID y se resuelve aquí contra el catálogo CON inactivos, en el idioma
 * activo: la columna enseña Fundamentales HISTÓRICOS y con el catálogo activo uno retirado caería
 * a su GUID.
 */
function filasDeRanking(
  entradas: ShineRankEntryDto[], nombreDeFundamental: (id: string) => string, traducir: Traductor,
): FilaRankingBrillamos[] {
  return entradas.map((entrada): FilaRankingBrillamos => ({
    nombre: entrada.nombre,
    isSuprimido: !!entrada.suprimido,
    email: entrada.email,
    employeeId: entrada.employeeId,
    area: entrada.area,
    ouName: entrada.ouName,
    site: entrada.site,
    fundamental: entrada.fundamentalId ? nombreDeFundamental(entrada.fundamentalId) : null,
    count: entrada.count,
    crossOu: entrada.crossOuCount == null
      ? SIN_DATO
      : traducir('excel.crossOuCount', { count: entrada.crossOuCount }),
    tipoDominante: entrada.tipoDominante,
  }));
}

/**
 * Las 7 hojas del libro, en el orden de la plantilla del cliente. Cada hoja de detalle cae a su
 * hoja de hueco documentado cuando la campaña está genuinamente sin datos (sin reconocimientos, o
 * sin nadie que haya reconocido), nunca a una pestaña vacía ni a cifras fabricadas.
 */
function construirLibroBrillamos(opciones: {
  traducir: Traductor;
  t: TFunction<'recognition'>;
  report: CampaignReportDto;
  metadatos: MetadatosBrillamos;
  summary: CampaignSummaryDto | undefined;
  isSummarySuprimido: boolean;
  tasaParticipacion: number | null;
  resolvedRanking: ShineRankEntryDto[];
  resolvedTopReconocedores: ShineRankEntryDto[];
  nominaciones: NominationRawExportRowDto[];
  fundamentalesConHabitos: FundamentalConHabitosResueltos[];
  rankingHabitosTodos: RankedEntryDto[];
  padreDelHabito: Map<string, string>;
  nombreDeFundamental: (id: string) => string;
  colorDeFundamental: (id: string) => string;
  nombreDeHabito: (id: string) => string;
  /** D4 / Error 3 — ver `puntosParaElLibro`. */
  puntos: PuntosDelLibro;
  /**
   * 2026-10-01 — filas de «Todos los reconocimientos Recibidos»: lo que recibió la gente de la OU
   * filtrada (o la del administrador). `undefined` = sin OU que aplicar, la hoja no va.
   * Sesión 2026-10-02 (Error 2) — de TODAS las campañas Brillamos del periodo, no solo de esta.
   */
  nominacionesRecibidas?: NominationRawExportRowDto[];
  /** Sesión 2026-10-02 (Error 2) — nombre de la campaña de cada fila de Recibidos (columna Campaign). */
  nombreDeCampanaPorId?: (campaignId: string | null | undefined) => string;
  /** 2026-10-05 — columna «Puntos» de las hojas de reconocimientos (ver `puntosDeLaFila`). */
  puntosDe?: (nominacion: NominationRawExportRowDto) => ExcelCellValue;
  /** 2026-10-01 — «Sí»/«No» de la fila «La campaña otorga puntos» de Definiciones. */
  otorgaPuntos?: string;
}): ExcelSheetSpec[] {
  const {
    traducir, t, report, metadatos, summary, isSummarySuprimido, tasaParticipacion, resolvedRanking,
    resolvedTopReconocedores, nominaciones, fundamentalesConHabitos, rankingHabitosTodos,
    padreDelHabito, nombreDeFundamental, colorDeFundamental, nombreDeHabito, puntos,
    nominacionesRecibidas, otorgaPuntos, nombreDeCampanaPorId, puntosDe,
  } = opciones;

  const topFundamentales = report.topFundamentales ?? [];
  const hojaDistribucion = topFundamentales.length > 0
    ? buildDistribucionFundamentalSheet({
        traducir,
        topFundamentales,
        total: report.totalNominaciones,
        nombreDeFundamental,
        colorDeFundamental,
        rankingHabitosTodos,
        padreDelHabito,
        nombreDeHabito,
      })
    : buildHojaSinDatos(
        traducir('excel.sheetDistribution'),
        traducir('excel.distributionTitle'),
        traducir('excel.distributionEmpty'),
      );

  const embudo = report.embudo ?? [];
  const hojaEmbudo = embudo.length > 0
    ? buildEmbudoSheet(traducir, embudo)
    : buildHojaSinDatos(
        traducir('excel.sheetFunnel'), traducir('excel.funnelTitle'), traducir('excel.funnelEmpty'),
      );

  const hojaTopReconocedores = resolvedTopReconocedores.length > 0
    ? buildRankingSheet(
        traducir,
        traducir('excel.sheetTopRecognizers'),
        traducir('excel.topRecognizersTitle'),
        filasDeRanking(resolvedTopReconocedores, nombreDeFundamental, traducir),
      )
    : buildHojaSinDatos(
        traducir('excel.sheetTopRecognizers'),
        traducir('excel.topRecognizersTitle'),
        traducir('excel.topRecognizersEmpty'),
      );

  return [
    buildResumenEjecutivoSheet(
      traducir, metadatos, kpisDelLibro({ report, summary, isSummarySuprimido, tasaParticipacion, t, puntos }),
    ),
    buildDefinicionesSheet(traducir, otorgaPuntos),
    hojaDistribucion,
    hojaEmbudo,
    buildRankingSheet(
      traducir,
      traducir('excel.sheetTopRecognized'),
      traducir('excel.topRecognizedTitle'),
      filasDeRanking(resolvedRanking, nombreDeFundamental, traducir),
    ),
    hojaTopReconocedores,
    buildTodosLosReconocimientosSheet(traducir, nominaciones, fundamentalesConHabitos, undefined, { puntosDe }),
    // 2026-10-01 — la misma hoja, solo con lo que recibió la gente de la OU.
    ...(nominacionesRecibidas === undefined ? [] : [buildTodosLosReconocimientosSheet(
      traducir, nominacionesRecibidas, fundamentalesConHabitos, {
        nombre: traducir('excel.sheetReceivedRecognitions'),
        titulo: traducir('excel.receivedRecognitionsTitle'),
        subtitulo: traducir('excel.receivedRecognitionsSubtitle'),
      },
      { puntosDe, nombreDeCampana: nombreDeCampanaPorId ?? ((id) => id ?? SIN_DATO) },
    )]),
  ];
}

/** Opciones del libro salvo las filas de detalle (ver `opcionesComunesDelLibro` en `ShineTab`). */
type OpcionesComunesDelLibro = Omit<
  Parameters<typeof construirLibroBrillamos>[0], 'nominaciones' | 'nominacionesRecibidas'
>;

/**
 * 2026-10-13 — Opción B del análisis «Excel de Brillamos en backend»: con el interruptor encendido el
 * libro lo genera SurveyService (`POST v1/winners/brillamos-report/export`) y el navegador solo lo
 * descarga. Apagado (o sin definir), el armado de siempre en el navegador: es la reversa sin tocar el
 * backend. Solo se enciende donde el endpoint ya está desplegado.
 */
function excelDeBrillamosEnServidor(): boolean {
  return import.meta.env.VITE_BRILLAMOS_EXCEL_BACKEND === 'true';
}

/** Las hojas 1 a 6 del libro (Resumen Ejecutivo … Top Reconocedores); las siguientes son de detalle. */
const HOJAS_DE_RESUMEN = 6;

/**
 * 2026-10-13 — Cuerpo de la petición del libro generado en el servidor. Las hojas salen de
 * `construirLibroBrillamos` con las listas de detalle VACÍAS: así las hojas 1 a 6 y el encabezado de
 * las de detalle (banner, matriz de hábitos, columnas Puntos y Campaign) son exactamente los del
 * navegador, y el servidor solo les agrega las filas que lee con el alcance de quien exporta.
 */
export function peticionDelLibroEnServidor(opciones: {
  comunes: OpcionesComunesDelLibro;
  fileName: string;
  campaignId: string;
  /** BJT-1 — «Todas las campañas»: la lista va en lugar de `campaignId`. */
  campaignIds?: readonly string[];
  scopedParams: RecognitionReportParams;
  filtrosRecibidos: { ouIds: string[]; from?: string; to?: string } | undefined;
  filtrosDeDetalle: FiltrosDeDetalle;
  nombresDeCampana: Record<string, string>;
  puntos: { isCampanaConPuntos: boolean; textoSinPuntos: string };
}): BrillamosReportExportBody {
  const {
    comunes, fileName, campaignId, campaignIds, scopedParams, filtrosRecibidos, filtrosDeDetalle, nombresDeCampana, puntos,
  } = opciones;
  const hojas = construirLibroBrillamos({
    ...comunes,
    nominaciones: [],
    nominacionesRecibidas: filtrosRecibidos ? [] : undefined,
  });
  return {
    fileName,
    hojas: hojas.slice(0, HOJAS_DE_RESUMEN),
    enviados: {
      encabezado: hojas[HOJAS_DE_RESUMEN],
      ...(campaignIds?.length ? { campaignIds: [...campaignIds] } : { campaignId }),
      desde: scopedParams.startDate,
      hasta: scopedParams.endDate,
    },
    recibidos: filtrosRecibidos
      ? {
          encabezado: hojas[HOJAS_DE_RESUMEN + 1],
          ouIds: filtrosRecibidos.ouIds,
          desde: filtrosRecibidos.from,
          hasta: filtrosRecibidos.to,
          nombresDeCampana,
        }
      : undefined,
    codigosDeHabitos: comunes.fundamentalesConHabitos.flatMap((f) => f.habitos.map((h) => h.codigo)),
    puntos,
    filtros: filtrosDeDetalle,
  };
}

/**
 * D4 (2026-09-30) — tarjeta «Puntos otorgados». Fuera de `ShineTab` para no sumarle complejidad
 * cognitiva a una función de render que ya pasa del umbral de Sonar.
 */
function TarjetaDePuntos({ puntos }: Readonly<{ puntos: CampaignPointsDto | undefined }>) {
  const { t, i18n } = useTranslation('recognition');
  return (
    <div data-testid="puntos-campana">
      <KpiRow
        items={[{
          label: t('reports.shine.pointsAwarded'),
          value: puntos ? puntos.puntos.toLocaleString(i18n.language) : '—',
          color: 'var(--cp-orange)',
          sub: puntos
            ? t('reports.shine.pointsAwardedHint', { nominaciones: puntos.nominaciones, personas: puntos.personas })
            : undefined,
          subTone: 'muted',
        }]}
      />
    </div>
  );
}

/**
 * D4 — lo que recibe el libro: `undefined` = la fila no va (sin permiso o campaña sin puntos);
 * `null` = debía ir pero el total no respondió (se escribe «—»).
 */
/** Texto de «sin puntos» del libro: lo usan la fila de KPI y la columna por reconocimiento. */
const CLAVE_SIN_PUNTOS = 'reports.shine.excel.noPoints';

/**
 * 2026-10-05 — columna «Puntos» de «Todos los reconocimientos»: los puntos de CADA reconocimiento
 * (una fila por persona reconocida), del mismo libro y con el mismo alcance que la tarjeta «Puntos
 * otorgados» (RulesEngine: AdminGlobal/AdminBrillamos el tenant completo, AdminOUBrillamos su OU), así que
 * su suma cuadra con ella (mismo periodo; sin OU en la barra, porque la hoja no se recorta por OU):
 *   · la campaña no otorga puntos ⇒ «Sin puntos»;
 *   · el reconocimiento no acreditó, o queda fuera del alcance de quien exporta ⇒ el guion, nunca 0.
 *
 * 🔴 Ya NO hay «No disponible» (decisión del usuario, 2026-10-05: reporte informativo, confundía). La
 * fila de quien exporta muestra su número y la tarjeta también lo cuenta.
 */
function puntosDeLaFila(
  contexto: { otorga: boolean }, t: TFunction<'recognition'>,
): (nominacion: NominationRawExportRowDto) => ExcelCellValue {
  return (nominacion) => (contexto.otorga ? nominacion.puntos ?? SIN_DATO : t(CLAVE_SIN_PUNTOS));
}

/** Error 3 — lo que dicen la fila de puntos del Resumen Ejecutivo y su metadato bajo «Generado». */
export interface PuntosDelLibro {
  valor: ExcelCellValue;
  detalle?: string;
}

/**
 * 🔴 Sesión 2026-10-02 (Error 3) — la fila de puntos va SIEMPRE (antes se ocultaba, RN-P04):
 *   · la campaña no otorga puntos, o no otorgó ninguno ⇒ «Sin puntos»;
 *   · quien exporta no administra puntos, o el total no respondió ⇒ el guion de «sin dato».
 * 2026-10-05 — sin «No disponible» (ver `puntosDeLaFila`).
 */
function puntosParaElLibro(
  otorga: boolean, puedeVer: boolean, puntos: CampaignPointsDto | undefined, t: TFunction<'recognition'>,
): PuntosDelLibro {
  if (!otorga) return { valor: t(CLAVE_SIN_PUNTOS) };
  if (!puedeVer || !puntos) return { valor: SIN_DATO };
  if (puntos.puntos === 0) return { valor: t(CLAVE_SIN_PUNTOS) };
  return {
    valor: puntos.puntos,
    detalle: t('reports.shine.pointsAwardedHint', { nominaciones: puntos.nominaciones, personas: puntos.personas }),
  };
}

/** BJT-1 (2026-10-14) — el id que va a las APIs: vacío con «Todas las campañas», que mandan la lista. */
function idDeCampanaUnica(campaignId: string, campaignIds: readonly string[] | undefined): string {
  return campaignIds?.length ? '' : campaignId;
}

/** `canExportData` default `true`: ver nota en `DailyTab.tsx`. */
/**
 * BJ-F5 — una campaña FINALIZADA se reporta completa: se omite el rango de la tira. Fuera del
 * componente para no sumarle complejidad cognitiva (la función de render ya pasa del umbral de Sonar).
 */
function paramsDeLaCampana(
  params: RecognitionReportParams,
  campana: CampaignDto | null,
  modalidad: RecognitionModalidad,
  top: number,
  /** BJT-1 (2026-10-14) — «Todas las campañas»: la lista viaja en lugar del id de la campaña combinada. */
  campaignIds?: readonly string[],
): RecognitionReportParams {
  const sinFechas = campana?.estado === 'Finalizada' ? { startDate: undefined, endDate: undefined } : {};
  if (campaignIds?.length) {
    return {
      ...params, rankingOuIds: undefined, ...sinFechas, campaignId: undefined, campaignIds: [...campaignIds], modalidad, top,
    };
  }
  /*
   * 🔴 BJS-12 (sesión 2026-10-02) — la pantalla NO se acota a la OU del administrador: con
   * `rankingOuIds` la tabla del ranking salía vacía para quien administra una OU sin reconocidos en
   * la campaña (BUG-9). Esa OU solo acota la hoja «Top Reconocidos» del Excel (`rankingParaElLibro`).
   */
  return { ...params, rankingOuIds: undefined, ...sinFechas, campaignId: campana?.id ?? '', modalidad, top };
}

/**
 * BJS-12 — «Top Reconocidos» del Excel: con la OU del administrador (`rankingOuIds`, solo cuando la
 * barra no trae OU) se pide el ranking acotado a ella y se le resuelven los nombres; sin ella, el
 * mismo ranking de la pantalla.
 */
async function rankingParaElLibro(
  scopedParams: RecognitionReportParams,
  rankingOuIds: readonly string[] | undefined,
  rankingDePantalla: ShineRankEntryDto[],
): Promise<ShineRankEntryDto[]> {
  if (!rankingOuIds?.length) return rankingDePantalla;
  const acotado = await recognitionReportsApi.getCampaign({ ...scopedParams, rankingOuIds: [...rankingOuIds] });
  const ids = [...new Set(acotado.ranking.map((r) => r.id).filter(Boolean))];
  let nombres = new Map<string, string>();
  try {
    if (ids.length > 0) nombres = new Map((await userAdminApi.batchLookupUsers(ids)).map((p) => [p.id, p.displayName]));
  } catch {
    // Sin directorio, el nombre que ya conoce la pantalla y, si no, el id: nunca una fila sin persona.
  }
  const dePantalla = new Map(rankingDePantalla.map((r) => [r.id, r.nombre]));
  return withResolvedNames(acotado.ranking, (id) => nombres.get(id) ?? dePantalla.get(id) ?? id);
}

/** BJ-D2 — el filtro de fechas activo sobre una campaña finalizada no aplica y se avisa. */
function fechasQueNoAplican(campana: CampaignDto | null, params: RecognitionReportParams): FiltroDeTira[] {
  const hayFechas = Boolean(params.startDate || params.endDate);
  return campana?.estado === 'Finalizada' && hayFechas ? ['fechas'] : [];
}

/** BJ-D7 — posición absoluta de la fila `i` de la página actual (no reinicia en cada página). */
/**
 * 2026-09-25 — conciliación Resumen Ejecutivo ↔ «Todos los reconocimientos»: la hoja de detalle se
 * pide SOLO por campaña (`/v1/winners/nominations-export`, sin periodo), mientras los KPIs respetan
 * el rango de la tira. Sin este recorte la hoja traía nominaciones fuera del periodo y el conteo de
 * filas no podía cuadrar con «Total de nominaciones». Compara por día (AAAA-MM-DD, inclusivo), el
 * mismo grano del selector de fechas. Sin rango (campaña finalizada, BJ-F5) no recorta nada.
 */
function nominacionesDelPeriodo(
  filas: NominationRawExportRowDto[],
  params: RecognitionReportParams,
): NominationRawExportRowDto[] {
  const { startDate, endDate } = params;
  if (!startDate && !endDate) return filas;
  return filas.filter((fila) => {
    const dia = fila.date.slice(0, 10);
    return (!startDate || dia >= startDate) && (!endDate || dia <= endDate);
  });
}

/**
 * 2026-10-01 — «Todos los reconocimientos Recibidos»: lo que RECIBIÓ la gente de la OU filtrada en la
 * barra o, sin OU elegida, de la OU del administrador. Sin ninguna de las dos, la hoja no va
 * (`undefined`): repetiría «Todos los reconocimientos» bajo otro nombre.
 *
 * 🔴 Sesión 2026-10-02 (Error 2) — ya NO se filtra el libro de ESTA campaña: la campaña se asigna por
 * la OU de quien ENVÍA, así que lo que recibió la gente de FOSE vive en las campañas de Europa y México,
 * y la hoja salía vacía mientras el Dashboard de Recibidos sí lo mostraba. Se pide al export de recibidos
 * (todas las campañas Brillamos) con el periodo del libro: el rango de la tira o, sin él, la vigencia
 * de la campaña.
 */
function filtrosDeRecibidos(
  params: RecognitionReportParams,
  scopedParams: RecognitionReportParams,
  campana: CampaignDto | null,
): { ouIds: string[]; from?: string; to?: string } | undefined {
  const ous = params.ouIds?.length ? params.ouIds : params.rankingOuIds;
  if (!ous?.length) return undefined;
  return {
    ouIds: [...ous],
    from: scopedParams.startDate ?? campana?.fechaInicio?.slice(0, 10),
    to: scopedParams.endDate ?? campana?.fechaFin?.slice(0, 10),
  };
}

/** Clave i18n del rótulo de cada modalidad del selector «Filtro Brillamos». */
const CLAVE_DE_MODALIDAD: Record<Exclude<RecognitionModalidad, 'todos'>, string> = {
  personaUnica: 'reports.shine.modalidadPersonaUnica',
  grupal: 'reports.shine.modalidadGrupal',
  crossOu: 'reports.shine.modalidadCrossOu',
};

/**
 * 2026-10-09 — «Alcance» del Resumen Ejecutivo + los filtros de la tira que acotan el libro entero
 * (persona, Fundamental, modalidad y Site). Desde el 2026-09-29 esa celda solo decía el alcance de la
 * campaña, así que un libro filtrado por una persona no lo declaraba en ningún lado: «16
 * reconocimientos» se leía como el total de la campaña. Sin filtros, el texto es el de siempre.
 */
function alcanceConFiltrosDeLaTira(opciones: {
  alcance: string;
  params: RecognitionReportParams;
  t: TFunction<'recognition'>;
  nombreDeFundamental: (id: string) => string;
  nombreDePersona: (id: string) => string;
}): string {
  const { alcance, params, t, nombreDeFundamental, nombreDePersona } = opciones;
  const conPersonaYFundamental = conFiltrosDeTira(
    alcance, params, nombreDeFundamental, nombreDePersona,
    { fundamental: t('reports.filters.fundamental'), persona: t('reports.filters.person') },
  );
  const extra: string[] = [];
  if (params.modalidad && params.modalidad !== 'todos') {
    extra.push(`${t('reports.shine.modalidadSelector')}: ${t(CLAVE_DE_MODALIDAD[params.modalidad])}`);
  }
  if (params.site) extra.push(`${t('reports.filters.site')}: ${params.site}`);
  return [conPersonaYFundamental, ...extra].join(' · ');
}

/** Texto del «Periodo» del libro: el rango de la tira o, en una campaña finalizada, su vigencia. */
function periodoDelLibro(params: RecognitionReportParams, campana: CampaignDto | null): string {
  if (params.startDate || params.endDate) return [params.startDate, params.endDate].filter(Boolean).join(' – ');
  const vigencia = [campana?.fechaInicio, campana?.fechaFin].filter(Boolean).map((f) => f!.slice(0, 10));
  return vigencia.length > 0 ? vigencia.join(' – ') : SIN_DATO;
}

function posicionEnPagina(pag: { pagina: number; tamano: number }, i: number): number {
  return (pag.pagina - 1) * pag.tamano + i + 1;
}

/** BJ-D1/BJ-D6 — texto y conteo del alcance ya truncado; vacío = campaña global. */
function resumenDelAlcance(
  alcance: readonly string[],
  nombreDeOu: (id: string) => string,
  todas: string,
): { texto: string; conteo: number | string } {
  if (alcance.length === 0) return { texto: todas, conteo: todas };
  return { texto: alcance.map(nombreDeOu).join(', '), conteo: alcance.length };
}

export function ShineTab(
  {
    params, canExportData = true, campana = null, alcanceVisible = [], avisoDeAlcance = null, campaignIds,
    onLimpiarCampana,
  }: Readonly<{
    params: RecognitionReportParams;
    canExportData?: boolean;
    /**
     * 2026-09-24 — campaña elegida en la BARRA de filtros (BJ-D3: el selector subió al principio de
     * la tira; vive en `RecognitionAnalyticsView` junto con el estatus Activa/Finalizada y la
     * cascada por OU). `null` = sin campaña ⇒ el tab pide elegir una (BJ-F1).
     */
    campana?: CampaignDto | null;
    /** Alcance de la campaña ya truncado al nivel más alto (BJ-H4), para «Alcance de la campaña». */
    alcanceVisible?: readonly string[];
    /**
     * BJF-1 (2026-10-14) — aviso ya traducido de que la campaña solo cubre su alcance (con «Todas las
     * OUs» o con OUs elegidas fuera de él). `null` = no hay nada que avisar.
     */
    avisoDeAlcance?: string | null;
    /**
     * BJT-1 (2026-10-14) — «Todas las campañas»: ids de las campañas Brillamos que consolida la
     * campaña combinada de `campana`. Las consultas y los exports mandan esta lista, nunca su id.
     */
    campaignIds?: readonly string[];
    /** Quita la campaña elegida (botón de limpiar del bloque de alcance). */
    onLimpiarCampana?: () => void;
  }>,
) {
  const { t, i18n } = useTranslation('recognition');
  const lang = i18n.language.split('-')[0];
  // Fix round 1, hallazgo 1+2+3 — `handleExportShine` ahora hace hasta 25 idas y vueltas HTTP
  // (`fetchNominacionesCrudasPaginado`) antes de armar el workbook; sin este estado, un fallo a
  // mitad de camino quedaba como una promesa rechazada sin manejar (ningún mensaje, ningún log) y
  // un doble clic disparaba dos paginaciones completas en paralelo. Mismo patrón que
  // `exportandoNominaciones`/`exportError` en `AdminWinners.tsx`.
  const [exportandoShine, setExportandoShine] = useState(false);
  /** Clave i18n del aviso de error de «Exportar Brillamos»; `null` = sin error. */
  const [exportShineError, setExportShineError] = useState<string | null>(null);
  // Fix final (revisión de rama) — mismo hallazgo que arriba, pero en `handleExportWinners`: la
  // migración xlsx→exceljs volvió async a `exportToExcel` y ese handler quedó sin try/catch. Estado
  // PROPIO (no se reusa `exportandoShine`/`exportShineError`): son dos descargas independientes —
  // "Exportar Brillamos" y "Exportar ganadores" — y compartir el flag bloquearía un botón por el
  // fallo del otro.
  //
  // 🔴 Fix ronda 2 (revisión) — el `try/catch` NO bastaba aquí: `winners` es un `useQuery({ enabled:
  // false, retry: 0 })` (`useWinnersExport`, `hooks.ts`) y `refetch()` de TanStack Query v5 por
  // defecto tiene `throwOnError: false` — ante un 401/500 RESUELVE con `{ data: undefined, isError:
  // true, error }`, NUNCA rechaza la promesa. El `try/catch` nunca se disparaba: `res.data ?? []`
  // caía a `[]`, `exportToExcel([], …)` corría "bien" y el usuario se llevaba un `.xlsx` vacío con
  // apariencia de éxito — peor que el bug original, que al menos era detectable como fallo. Se pide
  // `refetch({ throwOnError: true })` para que el fallo SÍ rechace la promesa y el `try/catch` de
  // abajo lo atrape de verdad (mismo criterio que `handleExportShine`, cuyas llamadas SÍ rechazan
  // porque no pasan por `refetch`).
  const [exportandoWinners, setExportandoWinners] = useState(false);
  const [exportWinnersError, setExportWinnersError] = useState(false);

  // T-10 — el tab es POR CAMPAÑA (confirmado con Adriana, 18-ago-2026), y hasta ahora no había forma
  // de elegir una: `params` solo trae ou/fechas/fundamental, así que el backend recibía `campaignId`
  // vacío, devolvía el agregado del scope con el nombre literal «Brillamos» y los tres KPIs que
  // dependen del resumen por campaña pintaban «—» para siempre. El selector es la pieza que faltaba.
  const campaignId = campana?.id ?? '';
  // BJT-1 — con «Todas las campañas» `campaignId` es el de la campaña combinada (solo sirve para saber
  // que hay campaña elegida): a las APIs va la lista y ningún id único.
  const esTodasLasCampanas = Boolean(campaignIds?.length);
  const campaignIdUnico = idDeCampanaUnica(campaignId, campaignIds);
  // BJ-F5 — el rango de fechas solo aplica a campañas ACTIVAS (ver `paramsDeLaCampana`): sin él, el
  // backend toma su ventana máxima y, como los recibos ya se filtran por la campaña, el resultado es
  // su periodo completo (Adriana 39:30, Rubi 40:00/41:37).
  /*
   * Maqueta — select «Filtro Brillamos» sobre el Ranking de Nominados: todos / solo persona única /
   * cross-OU. Vive en el TAB y no en la barra de filtros global porque es específico de esta tabla:
   * la barra la comparten siete pestañas y ninguna otra sabe qué hacer con esto.
   *
   * ⚠️ Este filtro sale solo del prototipo, no de la Sección 7.
   */
  const [modalidad, setModalidad] = useState<RecognitionModalidad>('todos');
  /*
   * Tamaño del ranking de nominados. Manda el `top` de la petición (50 por omisión en este tab,
   * techo 200), no un recorte del cliente. Control de TAMAÑO y no de páginas: el backend no tiene
   * offset.
   */
  /*
   * Filas que se PIDEN. Fijo, no un control en pantalla — el mismo criterio que el tab Diario desde el
   * 25-ago: las tablas paginan en cliente, así que un selector que dispare consulta por cada cambio de
   * página sobra. El valor es el «Top 200» de las hojas «Definiciones» del cliente, que el handler
   * clampea ahí de todos modos.
   */
  const TOP_PEDIDO = 200;

  const scopedParams = paramsDeLaCampana(params, campana, modalidad, TOP_PEDIDO, campaignIds);

  // Nombre legible de la campaña elegida.
  const nombreDeCampana = campana ? getCampaignName(campana, lang) : '';
  // Sesión 2026-10-02 (Error 2) — nombres de campaña para la columna Campaign de «Recibidos»: es la
  // misma lista (misma clave de caché) que ya pide el selector de campaña de la barra.
  const campanasDelTenant = useCampaigns(undefined, 1, 100, Boolean(campana));
  // 2026-10-13 — la misma tabla como objeto: viaja al servidor cuando el libro se genera allá.
  const nombresDeCampanaPorId = useMemo(
    () => Object.fromEntries((campanasDelTenant.data?.items ?? []).map((c) => [c.id, getCampaignName(c, lang)])),
    [campanasDelTenant.data, lang],
  );
  const nombreDeCampanaPorId = useMemo(
    () => (id: string | null | undefined) => (id ? nombresDeCampanaPorId[id] ?? id : SIN_DATO),
    [nombresDeCampanaPorId],
  );

  /**
   * BJ-F1 (War Room 2026-09-21) — la campaña es OBLIGATORIA y va primero: sin ella no se consulta
   * nada y se pide elegirla («Selecciona primero una campaña»).
   *
   * 🔴 Esto REEMPLAZA la regla del 2026-09-04, que encendía la consulta también con Fundamental o
   * persona sin campaña. Con el alcance de la campaña mandando sobre el resto (BJ-F2), un reporte sin
   * campaña no tiene alcance que respetar.
   */
  const consultaHabilitada = Boolean(campaignId);
  /** Filtro de fechas activo sobre una campaña finalizada: no aplica y se avisa (BJ-D2). */
  const fechasNoAplican = useMemo(() => fechasQueNoAplican(campana, params), [campana, params]);
  const filtrosActivos = useMemo(() => filtrosActivosDe(params), [params]);
  /**
   * 2026-09-04 — solo la persona: a diferencia de «Total de plantilla» (que no se acota ni por
   * Fundamental ni por persona), los cuatro KPIs de arriba («Reconocimientos realizados», «Personas
   * únicas reconocidas», «Personas únicas que reconocen», «Tasa de participación») SÍ se acotan por
   * Fundamental desde que `getCampaignSummary` volvió a recibir ese parámetro — el único filtro que
   * el endpoint `campaign-summary` no acepta es la persona. Mismo patrón que el embudo de `DailyTab`.
   */

  const q = useRecognitionCampaign(scopedParams, consultaHabilitada);
  const report = q.data;
  // Tarea 15 — el lote de ids a resolver incluye también `topReconocedores`: el backend manda
  // `Nombre: entrada.ReceptorId` para esas filas (mismo patrón crudo que `ranking`, ver
  // `GetRecognitionCampaignQueryHandler.cs`), así que sin sumarlos aquí el export de «Top
  // Reconocedores» repetiría el bug de GUID crudo que T-10 ya encontró y corrigió para el ranking.
  const nomineeIds = useMemo(() => {
    const nominados = report?.ranking.map((entrada) => entrada.id) ?? [];
    const reconocedores = report?.topReconocedores?.map((entrada) => entrada.id) ?? [];
    // Punto 6 (revisión final) — `params.personaId` se suma al lote: sin esto, «Filtros aplicados»
    // del export mostraría el id crudo si la persona elegida no aparece en ningún ranking.
    const persona = params.personaId ? [params.personaId] : [];
    return [...nominados, ...reconocedores, ...persona];
  }, [report, params.personaId]);
  const { resolvePersonName } = usePersonNameResolver(nomineeIds);
  const { resolveOuName } = useOuNameResolver();
  const alcance = resumenDelAlcance(alcanceVisible, resolveOuName, t('reports.common.scopeAll'));
  const resolvedRanking = useMemo(
    () => (report ? withResolvedNames(report.ranking, resolvePersonName) : []),
    [report, resolvePersonName],
  );
  const resolvedTopReconocedores = useMemo(
    () => (report?.topReconocedores ? withResolvedNames(report.topReconocedores, resolvePersonName) : []),
    [report, resolvePersonName],
  );

  const live = useRecognitionCampaignLive(scopedParams, Boolean(campaignId));
  // BJS-8/9 — los mismos resolutores (insignia, año, Fundamental) que el Excel del módulo Ganadores.
  const resolutoresDeGanador = useResolutoresDeGanador();
  /*
   * BJS-7 (sesión 2026-10-02) — «Exportar ganadores» solo con la campaña EXPIRADA (`Finalizada`): en
   * una activa todavía no hay ganadores y el botón bajaba un archivo vacío (BUG-8).
   */
  const ganadoresDisponibles = campana?.estado === 'Finalizada';
  const [ganadoresVacio, setGanadoresVacio] = useState(false);

  // E4 — los 5 KPIs unificados de la spec 4.3 (Reconocimientos realizados / Personas únicas
  // reconocidas / Personas únicas que reconocen / Tasa de participación / Total de plantilla) salen
  // de `CampaignSummaryDto` (mismo endpoint GetCampaignSummary que ya consume `CampaignsGeneralTab`),
  // no de `GetCampaign`/`useRecognitionCampaignLive`: esos KPIs "de hoy" (nominaciones/OUs activas/
  // nominaciones en vivo/días restantes) no correspondían a ninguno de los 5 de la spec.
  // 2026-09-04 — el resumen vuelve a acotarse por Fundamental: el ranking de abajo ya lo hacía y los
  // 5 KPIs no, así que quedaban leyendo la campaña completa encima de un ranking filtrado. `Audiencia`
  // es la única cifra que el backend deja sin acotar (ver nota de `AvisoFiltroNoAplica` más abajo).
  //
  // 🔴 D1 (2026-09-30, reporte del cliente) — las tarjetas se piden con el MISMO recorte que el ranking
  // (`scopedParams`: fechas, OU de quien recibe, modalidad, persona y diarios reutilizados). Antes el
  // resumen contaba la campaña completa sin la OU de la barra mientras el ranking contaba la semana:
  // 8133 reconocimientos arriba y 3 filas abajo, y una persona con 3 avisos aparecía con 1.
  const summaryQuery = useCampaignSummaryDelReporte(scopedParams, consultaHabilitada);
  const summary = summaryQuery.data;

  // D4 (2026-09-30) — puntos de la campaña: solo administradores de puntos y solo si la campaña los
  // otorga. Mismo recorte de fechas y OU que el reporte: desde BJQ-8 (2026-10-05) RulesEngine lee las
  // OUs de la barra como «la OU participó» (envía o recibe), igual que AnalyticsService.
  const roles = useEffectiveRoles();
  const verPuntos = puedeVerPuntos(roles) && campanaConPuntos(campana);
  const puntosQuery = useCampaignPoints(
    campaignIdUnico,
    { from: scopedParams.startDate, to: scopedParams.endDate, ouIds: scopedParams.ouIds, campaignIds },
    verPuntos,
  );
  const puntosDelLibro = puntosParaElLibro(campanaConPuntos(campana), puedeVerPuntos(roles), puntosQuery.data, t);
  const isSummarySuprimido = summary?.suprimido ?? false;
  const tasaParticipacion = tasaDeParticipacion(summary?.participantes, summary?.audiencia);

  // Tabla «Ranking Daily Habits» en pantalla (Tarea 14b: en el Excel ya no es su propia hoja, se
  // pliega en «Distribución Fundamental»). El backend manda el ID en `nombre` porque el catálogo de
  // hábitos vive en SurveyService y AnalyticsService no lee su base (regla de oro #2); aquí se
  // traduce con el catálogo que la pantalla ya carga. Un id que el catálogo no conozca se
  // muestra TAL CUAL en vez de desaparecer: un hábito retirado sigue siendo un dato real.
  // `isLoading` (fix round 1, hallazgo 5) — gatea el botón de exportar (más abajo): sin esto, un
  // clic mientras la query del catálogo aún no resuelve arma `buildTodosLosReconocimientosSheet`
  // con `catalogoReporteria` en `undefined`, y la matriz de hábitos sale con CERO columnas en
  // silencio, sin que nadie lo note.
  // `isError` (fix final, hallazgo catálogo-falla-en-silencio) — mismo riesgo que `isLoading`, pero
  // cuando la query FALLA (no solo tarda): `isLoading` cae a `false` con `data` en `undefined`, así
  // que sin esto el botón se reactivaba con el catálogo vacío y `nombreDeFundamental` caía en su
  // fallback de "Fundamental retirado" para CADA fila de "Top Reconocidos"/"Top Reconocedores", la
  // matriz de hábitos de "Todos los reconocimientos" salía con CERO columnas, y "Distribución
  // Fundamental" perdía sus nombres — un archivo con datos afirmativamente incorrectos, no un hueco.
  const {
    data: catalogoReporteria, isLoading: catalogoCargando, isError: catalogoConError,
  } = useFundamentalsForReporting();
  /**
   * Nombre del hábito. Del catálogo CON inactivos por el mismo motivo que los Fundamentales: esta
   * tabla enseña hábitos HISTÓRICOS y con el catálogo activo uno retirado caería a su GUID.
   */
  const nombreDeHabito = useMemo(() => {
    const porId = new Map<string, string>();
    for (const f of catalogoReporteria ?? []) {
      for (const h of f.dailyHabits ?? []) {
        porId.set(h.id, h.names?.[lang] ?? h.names?.en ?? h.code ?? h.id);
      }
    }
    return (id: string) => porId.get(id) ?? id;
  }, [catalogoReporteria, lang]);

  /**
   * Nombre del Fundamental de cada fila. Del catálogo CON inactivos: la columna enseña Fundamentales
   * HISTÓRICOS y con el catálogo activo un Fundamental retirado caería a su GUID — el mismo defecto
   * que ya se corrigió en el bloque de barras.
   */
  const nombreDeFundamental = useMemo(() => {
    const porId = new Map<string, string>();
    for (const f of catalogoReporteria ?? []) {
      porId.set(f.id, f.names?.[lang] ?? f.names?.en ?? f.code ?? f.id);
    }
    // Sin nombre NO se enseña el id: se rotula como retirado, igual que el bloque de barras.
    return (id: string) => porId.get(id) ?? t('reports.daily.retiredFundamental');
  }, [catalogoReporteria, lang, t]);

  /**
   * Bug de producción 2026-08-26 — color de cada Fundamental para la gráfica de barras del export,
   * el MISMO color guardado en catálogo que ya usa `<DistribucionFundamental>` en pantalla
   * (`getFundamentalColor`, `f.color` de la base con fallback a la paleta por índice) — no un color
   * único inventado para el export.
   */
  const colorDeFundamental = useMemo(() => {
    const porId = new Map<string, string>();
    (catalogoReporteria ?? []).forEach((f, indice) => {
      porId.set(f.id, getFundamentalColor(f as never, indice));
    });
    return (id: string) => porId.get(id) ?? argbACss(excelColors.headerTablaFondo);
  }, [catalogoReporteria]);

  /**
   * Fundamental padre de cada hábito, del catálogo CON inactivos. Es la misma relación que usa el
   * bloque de barras para su segundo nivel; aquí alimenta la columna «Fundamental» de la tabla de
   * hábitos y su filtro propio.
   */
  const padreDelHabito = useMemo(() => {
    const porId = new Map<string, string>();
    for (const f of catalogoReporteria ?? []) {
      for (const h of f.dailyHabits ?? []) porId.set(h.id, f.id);
    }
    return porId;
  }, [catalogoReporteria]);

  /**
   * Filtro propio de la tabla de hábitos. NO se toca el filtro global de Fundamental de la barra
   * superior: ese acota TODO el reporte (KPIs, ranking de nominados, distribución) y aquí la pregunta
   * es solo «de esta tabla, enséñame los hábitos de tal Fundamental». Mezclarlos haría que ordenar
   * una tabla cambiara las cifras de arriba.
   */
  const [habitoFundFiltro, setHabitoFundFiltro] = useState<string>('');

  const rankingHabitosTodos = report?.rankingHabitos ?? [];

  /** Fundamentales presentes en la tabla — solo esos se ofrecen: filtrar a un resultado vacío es ruido. */
  const fundamentalesConHabitos = useMemo(() => {
    const ids = new Set<string>();
    for (const h of rankingHabitosTodos) {
      const padre = padreDelHabito.get(h.id);
      if (padre) ids.add(padre);
    }
    return [...ids]
      .map((id) => ({ id, nombre: nombreDeFundamental(id) }))
      .sort((a, b) => a.nombre.localeCompare(b.nombre, i18n.language));
  }, [rankingHabitosTodos, padreDelHabito, nombreDeFundamental, i18n.language]);

  const rankingHabitos = habitoFundFiltro
    ? rankingHabitosTodos.filter((h) => padreDelHabito.get(h.id) === habitoFundFiltro)
    : rankingHabitosTodos;

  /*
   * Las CUATRO tarjetas de adopción de la maqueta, acotadas a la campaña por el backend.
   *
   * 🔴 `null` NO es 0 %. Un 0 % afirmaría que nadie de ese grupo nominó; `null` dice que no se pudo
   * calcular (el padrón no respondió, o no hay censo). Mismo criterio que el Diario, y la razón por la
   * que se reusa `pctAdopcion` en vez de recalcular aquí.
   */
  const tarjetasDeAdopcion = useMemo((): KpiItem[] => {
    const numero = (n: number) => n.toLocaleString(i18n.language);

    const deSegmento = (etiqueta: string, dato: AdopcionDto | null | undefined, color: string): KpiItem => {
      const pct = pctAdopcion(dato);
      if (pct === null) return { label: etiqueta, value: '—', color };
      return {
        label: etiqueta,
        // Fix (2 decimales) — ver el comentario de `kpiAdopcionSegmento`: mismo `.toFixed(2)` para
        // que la tarjeta en pantalla y su fila del Excel muestren el mismo formato.
        value: `${pct.toFixed(2)}%`,
        color,
        sub: t('reports.shine.adoptionHint', {
          reconocedores: numero(dato!.reconocedores),
          plantilla: numero(dato!.plantilla),
        }),
        subTone: 'muted',
      };
    };

    const total = report?.adopcionTotal;
    const pctTotal = pctAdopcion(total);
    // «Sin actividad» se DERIVA: plantilla − nominadores. No es un campo propio para que no pueda
    // desincronizarse de las dos cifras de las que sale.
    const sinActividad = total && total.plantilla > 0
      ? Math.max(0, total.plantilla - total.reconocedores)
      : null;

    return [
      deSegmento(t('reports.shine.adoptionStaff'), report?.adopcionStaff, '#342F82'),
      deSegmento(t('reports.shine.adoptionFrontline'), report?.adopcionFrontline, '#F08521'),
      {
        label: t('reports.shine.adoptionTotal'),
        value: total && total.plantilla > 0 ? numero(total.reconocedores) : '—',
        sub: pctTotal !== null
          ? t('reports.shine.adoptionTotalHint', {
              pct: `${pctTotal.toFixed(2)}%`, plantilla: numero(total!.plantilla),
            })
          : undefined,
        subTone: 'muted',
      },
      {
        label: t('reports.shine.noActivity'),
        value: sinActividad !== null ? numero(sinActividad) : '—',
        color: 'var(--cp-red)',
        sub: t('reports.shine.noActivityHint'),
        subTone: 'muted',
      },
    ];
  }, [report, i18n.language, t]);

  // Una paginación por tabla, independientes: la maqueta pinta un pie en cada una.
  const pagNominados = usePaginacion(resolvedRanking);
  const pagHabitos = usePaginacion(rankingHabitos);

  const isEmpty = !!report && report.totalNominaciones === 0 && report.ranking.length === 0;

  /**
   * Lo que el libro necesita, salvo las filas de detalle: lo comparten el armado en el navegador y
   * la petición al servidor, así las hojas 1 a 6 salen de los mismos builders en los dos caminos.
   */
  async function opcionesComunesDelLibro(): Promise<OpcionesComunesDelLibro> {
    return {
      traducir: (clave: string, opciones?: Record<string, unknown>) => (
        t(`reports.shine.${clave}`, opciones ?? {})
      ),
      t,
      report: report!,
      metadatos: {
        // NO se usa `report.nombre`: el backend hace `Nombre: query.CampaignId ?? "Brillamos"`
        // (`GetRecognitionCampaignQueryHandler.cs`), así que ese campo es el GUID de la campaña,
        // nunca su nombre legible — el mismo defecto que ya se evitó en pantalla.
        nombreDeCampana,
        periodo: periodoDelLibro(scopedParams, campana),
        // 2026-09-29 (documento de Adriana) — en lugar de «Filtros aplicados», el ALCANCE de la
        // campaña: las OUs reales de su alcance (el mismo texto del bloque «Alcance de la campaña»).
        alcance: alcanceConFiltrosDeLaTira({
          alcance: alcance.texto, params: scopedParams, t, nombreDeFundamental, nombreDePersona: resolvePersonName,
        }),
        generado: new Date().toLocaleString(i18n.language),
        otorgaPuntos: campanaConPuntos(campana) ? t('reports.common.yes') : t('reports.common.no'),
      },
      summary,
      isSummarySuprimido,
      tasaParticipacion,
      resolvedRanking: await rankingParaElLibro(scopedParams, params.rankingOuIds, resolvedRanking),
      resolvedTopReconocedores,
      // La matriz de hábitos se filtra a Fundamentales/Hábitos ACTIVOS: el backend solo emite una
      // clave por Daily Habit activo al momento del export, así que una columna fija para uno
      // retirado saldría siempre en blanco y se leería como «nadie lo marcó nunca».
      fundamentalesConHabitos: resolverFundamentalesConHabitos(catalogoReporteria, lang),
      rankingHabitosTodos,
      padreDelHabito,
      nombreDeFundamental,
      colorDeFundamental,
      nombreDeHabito,
      puntos: puntosDelLibro,
      nombreDeCampanaPorId,
      puntosDe: puntosDeLaFila({ otorga: campanaConPuntos(campana) }, t),
      otorgaPuntos: campanaConPuntos(campana) ? t('reports.common.yes') : t('reports.common.no'),
    };
  }

  async function handleExportShine() {
    if (!report) return;

    // Fix round 1, hallazgo 1+2+3 — `handleExportShine` hace hasta 25 idas y vueltas HTTP
    // (`fetchNominacionesCrudasPaginado`) para «Todos los reconocimientos»: sin try/catch un 401/500
    // a mitad de camino rechazaba la promesa sin manejar (ningún mensaje, ningún log), y sin
    // `exportandoShine` el botón (más abajo) seguía habilitado durante toda la descarga, invitando
    // un doble clic que dispara dos paginaciones completas en paralelo. Mismo patrón que
    // `exportarNominacionesCrudas` en `AdminWinners.tsx`.
    setExportandoShine(true);
    setExportShineError(null);
    try {
      // 2026-10-09 — el detalle lleva los MISMOS filtros de la tira que los KPIs (persona, Fundamental,
      // modalidad, Site): sin ellos, con una persona elegida el Resumen hablaba de 16 reconocimientos y
      // «Todos los reconocimientos» traía la campaña entera (1 649).
      const filtrosDeDetalle = toFiltrosDeDetalle(scopedParams);
      // Error 2 — «Recibidos»: todas las campañas Brillamos del periodo (ver `filtrosDeRecibidos`).
      const filtrosRecibidos = filtrosDeRecibidos(params, scopedParams, campana);
      const comunes = await opcionesComunesDelLibro();
      // Nombre de la campaña, no su id crudo (bug QA 2026-09-10): sin esto, el archivo descargado
      // se identificaba solo por un GUID. Mismo criterio de sanitizado que `LeadersTab.tsx`.
      const fileName = nombreDeCampana
        ? `reporteria-brillamos-${nombreDeCampana.replace(/[^\p{L}\p{N}]+/gu, '-')}`
        : 'reporteria-brillamos';

      if (excelDeBrillamosEnServidor()) {
        const blob = await winnersApi.exportBrillamosReport(peticionDelLibroEnServidor({
          comunes, fileName, campaignId: campaignIdUnico, campaignIds, scopedParams, filtrosRecibidos, filtrosDeDetalle,
          nombresDeCampana: nombresDeCampanaPorId,
          puntos: { isCampanaConPuntos: campanaConPuntos(campana), textoSinPuntos: t(CLAVE_SIN_PUNTOS) },
        }));
        descargarBlobComoExcel(blob, `${fileName}.xlsx`);
        return;
      }

      // El export CRUDO paginado alimenta «Todos los reconocimientos», completo (sin tope desde RPT-6).
      const nominacionesDeLaCampana = await fetchNominacionesDeLasCampanas(
        esTodasLasCampanas ? campaignIds! : [campaignId], { filtros: filtrosDeDetalle });
      const recibidos = filtrosRecibidos
        ? await fetchRecibidosPaginado(filtrosRecibidos, { filtros: filtrosDeDetalle })
        : undefined;

      await exportMultiSheetExcel({
        fileName,
        sheets: construirLibroBrillamos({
          ...comunes,
          // Mismo periodo que los KPIs del Resumen Ejecutivo (ver `nominacionesDelPeriodo`).
          nominaciones: nominacionesDelPeriodo(nominacionesDeLaCampana, scopedParams),
          nominacionesRecibidas: recibidos,
        }),
      });
    } catch (error: unknown) {
      // Si falla, se DICE (mismo criterio que `AdminWinners.tsx`): sin esto, la promesa rechazada
      // no dejaba mensaje ni log, y quien exporta no tenía forma de saber si el archivo se generó.
      // Fix round 2 — el error también se loguea (guard DEV, mismo patrón que
      // `AdminWinners.tsx`/`CampaignRowActions.tsx`/`LeadersTab.tsx`): el banner le dice al usuario
      // QUE falló, pero sin esto un 401 y un 500 eran indistinguibles después del hecho.
      if (import.meta.env.DEV) console.error('No se pudo generar el export de Brillamos Juntos:', error);
      setExportShineError(claveDeErrorDeExportacion(error));
    } finally {
      setExportandoShine(false);
    }
  }

  /**
   * BJS-8/9 (sesión 2026-10-02) — el MISMO Excel que «Exportar ganadores» del módulo Ganadores
   * (`exportarGanadores.ts`), acotado a la campaña elegida. Reemplaza al export Crudo/Cascadeado
   * (BJS-6), que armaba otro archivo sin diseño y sin Site.
   */
  async function handleExportWinners() {
    setExportandoWinners(true);
    setExportWinnersError(false);
    setGanadoresVacio(false);
    try {
      const { filas } = await exportarGanadoresAExcel({
        filtros: { campaignId: campaignIdUnico || undefined },
        // BJT-1 — «Todas las campañas»: los ganadores de cada una, en un mismo archivo.
        campanas: esTodasLasCampanas ? campaignIds : undefined,
        t,
        resolutores: resolutoresDeGanador,
        // Nombre de la campaña, no su id crudo (bug QA 2026-09-10).
        nombreArchivo: nombreDeCampana
          ? `ganadores-${nombreDeCampana.replace(/[^\p{L}\p{N}]+/gu, '-')}`
          : 'ganadores',
      });
      if (filas === 0) setGanadoresVacio(true);
    } catch (error: unknown) {
      if (import.meta.env.DEV) console.error('No se pudo generar el export de ganadores de Brillamos:', error);
      setExportWinnersError(true);
    } finally {
      setExportandoWinners(false);
    }
  }

  return (
    <div className="au-card">
      <div className="au-card-header">
        <div>
          <div className="au-card-title">
            <i className="fas fa-star" aria-hidden="true" /> {t('reports.shine.title')}
          </div>
          <div className="au-card-sub">{t('reports.shine.subtitle')}</div>
        </div>
      </div>

      {/* 2026-09-24 — el selector de CAMPAÑA ya no vive aquí: subió al principio de la barra de
          filtros junto con el estatus Activa/Finalizada (BJ-D3/BJ-F4, `SelectorCampanaReporte`). En
          el panel solo queda la modalidad, que es propia de la tabla de nominados. */}
      <div className="rr-actions">
        {/* Maqueta — «Filtro Brillamos». Desde D1 (2026-09-30) acota el ranking Y las tarjetas: las
            dos piden el mismo recorte. */}
        <select
          className="au-cascade-select"
          value={modalidad}
          onChange={(e) => setModalidad(e.target.value as RecognitionModalidad)}
          aria-label={t('reports.shine.modalidadSelector')}
        >
          <option value="todos">{t('reports.shine.modalidadTodos')}</option>
          <option value="personaUnica">{t('reports.shine.modalidadPersonaUnica')}</option>
          {/* 2026-10-08 — complemento de «persona única»: reconocimientos a un equipo. */}
          <option value="grupal">{t('reports.shine.modalidadGrupal')}</option>
          <option value="crossOu">{t('reports.shine.modalidadCrossOu')}</option>
        </select>
      </div>

      {!consultaHabilitada ? (
        /*
         * Mismo criterio que «Históricos»: se pide elegir en vez de pintar los números del scope
         * bajo etiquetas que dicen «de la campaña».
         *
         * BJ-F1 (2026-09-24) — solo la campaña lo retira: «Selecciona primero una campaña». Antes
         * también lo retiraban el Fundamental o la persona de la tira (regla del 2026-09-04).
         */
        <div className="rr-state">{t('reports.shine.pickCampaign')}</div>
      ) : (
      <>
        {/*
          2026-09-24 — el alcance y el aviso de fechas van FUERA del estado del reporte: son contexto de
          la campaña elegida y deben verse también cuando el reporte sale vacío o cargando (una
          campaña finalizada sin recibos mostraba solo «No hay datos» y ocultaba por qué).
        */}
        {/*
          Banner naranja de la maqueta. Dice de dónde vienen los filtros de este panel: los de la
          tira de arriba se HEREDAN, y dentro del panel solo se elige campaña y modalidad. Sin él,
          alguien que no ve la tira (porque hizo scroll) no sabe que hay un recorte aplicado.

          «Limpiar todo» solo aparece si hay algo que limpiar: un botón que no hace nada invita a
          pulsarlo y a dudar de la pantalla cuando no pasa nada.
        */}
        {/*
          BJ-D1 (War Room 2026-09-21, Rubi 16:37) — antes «Filtros aplicados», que listaba lo
          marcado ARRIBA aunque las cifras salían del alcance de la campaña: la etiqueta mentía.
          Ahora dice «Alcance de la campaña» y enseña las OUs REALES de la campaña elegida,
          truncadas al nivel más alto (BJ-H4).
        */}
        <p className="rr-filtros-aplicados" data-testid="alcance-campana">
          <i className="fas fa-bullseye" aria-hidden="true" />
          <span>
            <strong>{t('reports.shine.campaignScope')}</strong>{' '}
            {alcance.texto}
          </span>
          {(campaignId || modalidad !== 'todos') && (
            <button
              type="button"
              className="rr-filtros-aplicados-limpiar"
              onClick={() => { onLimpiarCampana?.(); setModalidad('todos'); }}
            >
              {t('reports.shine.clearAll')}
            </button>
          )}
        </p>
        {/* BJF-1 — el reporte es POR CAMPAÑA: las OUs que la campaña no cubre no suman nada. */}
        {avisoDeAlcance && (
          <p className="rr-tira-nota" role="note" data-testid="aviso-alcance-campana">
            <i className="fas fa-circle-info" aria-hidden="true" /> {avisoDeAlcance}
          </p>
        )}
        {/* BJ-D2 — con una campaña finalizada el rango de fechas de la tira no aplica. */}
        <AvisoFiltroNoAplica filtros={fechasNoAplican} motivo="campanaFinalizada" />

      <ReportStatus
        isLoading={q.isLoading}
        isError={q.isError}
        error={q.error}
        isEmpty={isEmpty}
        onRetry={() => q.refetch()}
      >
        {report && (
          <>
            {(report.suprimido || live.data?.suprimido || isSummarySuprimido) && <SuppressedNote />}


            {/*
              🔴 NO se usa `report.nombre`: el backend hace `Nombre: query.CampaignId ?? "Brillamos"`,
              así que con campaña elegida devuelve el ID y la pantalla imprimía el GUID crudo
              (`8ee48bc749d8…`, visto en QA el 24-ago). El nombre ya lo tenemos aquí: es el mismo de
              la lista que alimenta el selector. Se cae a `report.nombre` solo si la campaña elegida
              ya no está en la lista, para no dejar el bloque vacío.
            */}
            <div className="au-card-sub rr-block-title">{nombreDeCampana}</div>

            {/*
              🔴 LOS CINCO KPIs DE LA SPEC 4.3 SE QUEDAN, aunque la maqueta pinte solo cuatro.
              «Personas únicas que reconocen», «Tasa de participación» y «Total de plantilla» son
              requisito de la Especificación, y en este proyecto la Especificación MANDA sobre lo que
              se pide en la maqueta o en UAT — ya invalidó dos peticiones por lo mismo. Quitarlos para
              parecerse a la maqueta habría retirado tres KPIs pedidos por escrito.
              Los dos de la maqueta que sí faltaban («Días restantes», «OUs activas») ya estaban, y la
              fila de adopción de abajo es lo genuinamente nuevo.

              2026-09-04 — «Total de plantilla» sale de esta fila (va justo debajo, con su propio
              aviso): es el único de los cinco que el backend NO acota por Fundamental. Los otros
              cuatro sí lo hacen desde que `getCampaignSummary` volvió a recibir `fundamentalId`
              (`ShineTab.tsx`, `hooks.ts`), así que dejan de necesitar el aviso de Fundamental — la
              «Tasa de participación» se recalcula sola contra la misma `Audiencia` sin acotar, que es
              precisamente lo que «participación» quiere decir (sobre la audiencia convocada).

              Con PERSONA sí queda un hueco real: `campaign-summary` no acepta `personaId` (decisión de
              producto — con una sola persona, «Personas únicas reconocidas» daría 1 y la «tasa de
              participación» compararía a esa persona contra la audiencia completa bajo una etiqueta
              que dice «participación», el mismo criterio por el que el embudo de `DailyTab` se calla
              con persona elegida). De ahí el aviso propio con `soloPersona`, no `filtrosActivos`: con
              solo Fundamental puesto los cuatro SÍ filtran y no deben avisar nada.
            */}
            {/* D1 (2026-09-30) — el resumen ya acepta la persona (mismo recorte que el ranking): el
                aviso «el filtro Persona no aplica» de esta fila se retiró. */}
            <KpiRow
              items={[
                {
                  label: t('reports.shine.recognitionsMade'),
                  value: summary && !isSummarySuprimido ? summary.reconocimientos : '—',
                  color: 'var(--cp-purple)',
                },
                {
                  /*
                   * T-10 — del RESUMEN de la campaña, no de `report.nominadosUnicos`.
                   *
                   * Ese campo es del SCOPE y de todos los orígenes: era el «75» que en UAT se leyó
                   * como «personas reconocidas en la campaña». Un número real bajo la etiqueta
                   * equivocada, que es peor que un hueco — el hueco se pregunta, el número se cree.
                   */
                  label: t('reports.shine.uniqueRecognized'),
                  value: summary && !isSummarySuprimido ? (summary.nominadosUnicos ?? '—') : '—',
                  color: 'var(--cp-cyan)',
                },
                {
                  /*
                   * T-10 — ya no es un hueco: `participantes` del resumen SON los emisores únicos de
                   * la campaña. Estaba calculado desde C2 y este tab lo ignoraba, pintando «—» al
                   * lado de un dato que ya viajaba en la respuesta que la pantalla pedía.
                   */
                  label: t('reports.shine.uniqueRecognizers'),
                  value: summary && !isSummarySuprimido ? (summary.participantes ?? '—') : '—',
                  color: 'var(--cp-orange)',
                },
                {
                  label: t('reports.shine.participationRate'),
                  value: !isSummarySuprimido && tasaParticipacion != null ? formatTasaDisplay(tasaParticipacion, false) : '—',
                  color: 'var(--cp-green)',
                },
                /*
                 * Los DOS de la maqueta que faltaban: «Días restantes» y «OUs activas».
                 *
                 * Se habían dejado fuera al cerrar E4, cuando la fila se alineó a los CINCO KPIs
                 * unificados de la spec 4.3 y estos dos —que son «de hoy», no del periodo— no
                 * correspondían a ninguno. La maqueta sí los pinta, y son aditivos: no desplazan a
                 * ninguno de los cinco ni cambian lo que miden.
                 *
                 * 🔴 No hace falta pedir nada: salen de `campaign-live`, que este tab YA consulta
                 * para el mismo campaignId. Estaban llegando y se descartaban.
                 *
                 * `DiasRestantes` es null cuando la campaña no tiene fecha de fin —«Fundamentales»
                 * no la tiene, es permanente— y eso NO es un cero: se pinta «—», porque «quedan 0
                 * días» diría que cerró hoy.
                 */
                {
                  label: t('reports.shine.daysLeft'),
                  value: live.data?.diasRestantes ?? '—',
                  color: 'var(--cp-purple)',
                },
                /*
                 * BJ-D6 (Rubi 16:54–18:20) — el contador sale del ALCANCE de la campaña: una
                 * campaña de Central marcaba 6 porque `campaign-live.ousActivas` cuenta las OUs con
                 * actividad en el scope consultado, no las de la campaña. Mismo número de OUs que
                 * lista «Alcance de la campaña» (nivel más alto); alcance vacío = global ⇒ «Todas».
                 */
                {
                  label: t('reports.shine.activeOus'),
                  value: alcance.conteo,
                  color: 'var(--cp-cyan)',
                },
              ]}
            />

            {/*
              «Total de plantilla» (Audiencia CONVOCADA por la campaña) es el único de los cinco KPIs
              de la spec 4.3 que el backend deja sin acotar por Fundamental ni por persona — de ahí su
              propio aviso, pegado SOLO a él y no a toda la fila de arriba, que sí responde al filtro.
            */}
            <AvisoFiltroNoAplica filtros={filtrosActivos} motivo="audienciaCampana" />
            <KpiRow
              items={[
                {
                  label: t('reports.shine.totalHeadcount'),
                  value: summary?.audiencia ?? '—',
                  color: 'var(--cp-red)',
                },
              ]}
            />

            {/* D4 (2026-09-30) — puntos otorgados: solo administradores de puntos y solo campañas
                con «Aplican puntos». Se oculta, no se pone en cero, cuando no aplica (RN-P04). */}
            {verPuntos && <TarjetaDePuntos puntos={puntosQuery.data} />}

            {/* 2026-09-29 (documento de Adriana) — sin la segunda «Plantilla actualizada»: es la
                misma que ya se ve arriba de la pantalla. */}

            {/* Las cuatro de adopción de la maqueta, acotadas a ESTA campaña. */}
            <KpiRow items={tarjetasDeAdopcion} />

            {/* BUG-7 (sesión 2026-10-02) — las tarjetas cuentan RECONOCIMIENTOS y el ranking,
                PERSONAS: con un reconocimiento grupal las dos cifras no tienen por qué coincidir. */}
            {report.ranking.length > 0 && (
              <p className="rr-filtros-aplicados" role="note" data-testid="nota-ranking-personas">
                <i className="fas fa-circle-info" aria-hidden="true" /> {t('reports.shine.rankingCountsPeople')}
              </p>
            )}
            {report.ranking.length === 0 ? (
              <div className="rr-state">{t('reports.common.empty')}</div>
            ) : (
              <div className="rr-table-wrap">
                <table className="rr-table">
                  <thead>
                    <tr>
                      <th>{t('reports.shine.colPos')}</th>
                      <th>{t('reports.shine.colName')}</th>
                      <th>{t('reports.shine.colOu')}</th>
                      <th>{t('reports.shine.colBu')}</th>
                      <th>{t('reports.shine.colDept')}</th>
                      <th>{t('reports.daily.colFundamental')}</th>
                      <th>{t('reports.shine.colOrigin')}</th>
                      <th className="rr-num">{t('reports.shine.colCount')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagNominados.visibles.map((r, i) => (
                      <tr key={r.id || i}>
                        {/* BJ-D7 (Rubi 10:49) — la posición NO reinicia en cada página: con el
                            paginador la página 2 empezaba otra vez en 1. */}
                        <td>{posicionEnPagina(pagNominados, i)}</td>
                        <td>{r.suprimido ? <span className="rr-muted">{t('reports.common.masked')}</span> : r.nombre}</td>
                        <td className="rr-rank-tag">{r.suprimido ? '' : (r.ouName ?? '')}</td>
                        <td className="rr-rank-tag">{r.suprimido ? '' : (r.buName ?? '')}</td>
                        <td className="rr-rank-tag">{r.suprimido ? '' : (r.deptName ?? '')}</td>
                        {/* Vacío, no un valor por defecto: el backend manda null cuando no consta. */}
                        <td className="rr-rank-tag">
                          {r.suprimido || !r.fundamentalId ? '' : nombreDeFundamental(r.fundamentalId)}
                        </td>
                        <td className="rr-rank-tag">
                          {r.suprimido ? '' : (r.origenes ?? []).join(' · ')}
                        </td>
                        <td className="rr-num--strong">{r.suprimido ? '—' : r.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/*
                  Paginación de PÁGINAS, como la maqueta («Mostrando 1-3 de 3» + página + 10 por pág.).
                  Es de cliente: el `top` de la petición pasa a ser fijo (el «Top 200» de las hojas
                  «Definiciones» del cliente) y esta tabla recorta lo suyo. Antes era solo un selector
                  de tamaño que disparaba una consulta por cada cambio.
                */}
                <Paginacion
                  siempreVisible
                  pagina={pagNominados.pagina}
                  totalPaginas={pagNominados.totalPaginas}
                  total={pagNominados.total}
                  tamano={pagNominados.tamano}
                  onPagina={pagNominados.setPagina}
                  onTamano={pagNominados.cambiarTamano}
                />
              </div>
            )}
            {/*
              * «Ranking Daily Habits» — tabla en pantalla (Tarea 14b: en el Excel ya no es su propia
              * hoja, se pliega como segundo nivel de «Distribución Fundamental»).
              *
              * Se pinta SOLO si hay datos: los agregados anteriores a la dimensión de hábitos no la
              * traen, y una tabla vacía con encabezados haría creer que nadie reconoce hábitos
              * cuando lo que pasa es que aún no se contaban.
              */}
            {rankingHabitos.length > 0 && (
              <div className="rr-table-wrap rr-table-wrap--top-gap">
                <div className="au-card-sub rr-block-title">
                  {t('reports.shine.habitsRanking')}
                </div>

                {/* Filtro PROPIO de esta tabla. Solo se ofrece si hay más de un Fundamental que
                    elegir: con uno solo, el selector no puede cambiar nada. */}
                {fundamentalesConHabitos.length > 1 && (
                  <label className="rr-habits-filter">
                    <span className="rr-sr-only">{t('reports.shine.filterHabitsByFundamental')}</span>
                    <select
                      className="rr-funnel-select"
                      value={habitoFundFiltro}
                      onChange={(e) => setHabitoFundFiltro(e.target.value)}
                    >
                      <option value="">{t('reports.shine.allFundamentals')}</option>
                      {fundamentalesConHabitos.map((f) => (
                        <option key={f.id} value={f.id}>{f.nombre}</option>
                      ))}
                    </select>
                  </label>
                )}

                <table className="rr-table">
                  <thead>
                    <tr>
                      <th>{t('reports.shine.colPos')}</th>
                      <th>{t('reports.shine.colHabit')}</th>
                      <th>{t('reports.daily.colFundamental')}</th>
                      <th className="rr-num">{t('reports.shine.colRecognitions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagHabitos.visibles.map((h, i) => {
                      const padre = padreDelHabito.get(h.id);
                      return (
                        <tr key={h.id}>
                          <td>{posicionEnPagina(pagHabitos, i)}</td>
                          <td>{nombreDeHabito(h.id)}</td>
                          {/* Vacío si el catálogo no sabe de qué Fundamental cuelga: no se adivina. */}
                          <td className="rr-rank-tag">{padre ? nombreDeFundamental(padre) : ''}</td>
                          <td className="rr-num">{h.count}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* La maqueta pinta un pie en ESTA tabla también, no solo en la de nominados. */}
                <Paginacion
                  siempreVisible
                  pagina={pagHabitos.pagina}
                  totalPaginas={pagHabitos.totalPaginas}
                  total={pagHabitos.total}
                  tamano={pagHabitos.tamano}
                  onPagina={pagHabitos.setPagina}
                  onTamano={pagHabitos.cambiarTamano}
                />
              </div>
            )}

            {/*
              * «Distribución por Fundamental» — el mismo bloque del tab Diario, con el mismo
              * componente: es la misma pregunta («¿por qué Fundamental se reconoce?») y tenerla con
              * dos aspectos distintos según la pestaña sería gratuito.
              *
              * Se pinta SOLO si viene: el campo es aditivo y un backend anterior no lo trae. Una
              * gráfica vacía se leería como «no se reconoció por ningún Fundamental», que es una
              * afirmación, y aquí no consta nada.
              *
              * El denominador es `totalNominaciones`, el mismo criterio que el Diario usa con
              * `totalPeriodo`. Los porcentajes pueden sumar más de 100 % y no es un error: un
              * reconocimiento puede llevar varios Fundamentales y cuenta en cada uno.
              */}
            {(report.topFundamentales?.length ?? 0) > 0 && (
              <>
                <h3 className="au-card-title rr-block-heading">
                  {t('reports.daily.fundamentalDistribution')}
                </h3>
                <DistribucionFundamental
                  variante="fina"
                  entradas={report.topFundamentales!}
                  total={report.totalNominaciones}
                  habitos={rankingHabitos}
                />
              </>
            )}

            <div className="rr-actions rr-actions--top-gap">
              {canExportData && (
                <button
                  type="button"
                  className="rr-btn"
                  onClick={handleExportShine}
                  // Fix round 1, hallazgo 1+3 (exportandoShine) — evita doble clic disparando dos
                  // paginaciones completas en paralelo. Hallazgo 5 (catalogoCargando) — sin el
                  // catálogo de Fundamentales cargado, `buildTodosLosReconocimientosSheet` armaría
                  // la matriz de hábitos con CERO columnas en silencio. `catalogoConError` (fix
                  // final) — mismo gate para cuando la query del catálogo FALLÓ (no solo tarda): sin
                  // esto el botón se reactivaba con el catálogo en `undefined` y el export salía con
                  // datos afirmativamente incorrectos en vez de bloquearse.
                  // 🔴 BJS-10 (sesión 2026-10-02) — se bloquea SOLO si la campaña no tiene
                  // reconocimientos: con `ranking.length === 0` quedaba bloqueado cada vez que el
                  // ranking salía vacío aunque hubiera reconocimientos que exportar (BUG-1).
                  disabled={
                    report.totalNominaciones === 0 || exportandoShine || catalogoCargando || catalogoConError
                  }
                >
                  <i
                    className={exportandoShine ? 'fas fa-spinner fa-spin' : 'fas fa-file-excel'}
                    aria-hidden="true"
                  />{' '}
                  {exportandoShine ? t('reports.shine.exporting') : t('reports.common.exportExcel')}
                </button>
              )}

              {canExportData && (
                <button
                  type="button"
                  className="rr-btn rr-btn--ghost"
                  onClick={handleExportWinners}
                  disabled={!ganadoresDisponibles || exportandoWinners}
                  title={ganadoresDisponibles ? undefined : t('reports.shine.winnersOnlyExpired')}
                >
                  <i
                    className={exportandoWinners ? 'fas fa-spinner fa-spin' : 'fas fa-trophy'}
                    aria-hidden="true"
                  />{' '}
                  {exportandoWinners ? t('reports.shine.exporting') : t('reports.shine.exportWinners')}
                </button>
              )}
            </div>

            {/* Fix round 1, hallazgo 1+2+3 — si `handleExportShine` falla (401/500 en cualquiera de
                las hasta 25 idas y vueltas HTTP del export crudo paginado), se AVISA. Mismo patrón
                que `ReportStatus`'s propio banner de error (`.rr-state--error`, `role="alert"`). */}
            {exportShineError && (
              <p className="rr-state rr-state--error" role="alert">
                <i className="fas fa-triangle-exclamation" aria-hidden="true" /> {t(exportShineError)}
              </p>
            )}

            {/* Fix final (revisión de rama) — mismo hallazgo, pero en `handleExportWinners`: quedaba
                sin manejo de error propio (Tarea 17 fix round 1 solo cubrió `handleExportShine`). */}
            {exportWinnersError && (
              <p className="rr-state rr-state--error" role="alert">
                <i className="fas fa-triangle-exclamation" aria-hidden="true" /> {t('reports.shine.exportError')}
              </p>
            )}
            {ganadoresVacio && (
              <p className="rr-state" role="status">
                <i className="fas fa-circle-info" aria-hidden="true" /> {t('reports.shine.winnersEmpty')}
              </p>
            )}

          </>
        )}
      </ReportStatus>
      </>
      )}
    </div>
  );
}
